// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Classic Forge Slot Machine
// 3-reel, 5-payline classic slot with provably fair engine
// ================================================================
import React, { useState, useCallback, useRef, useEffect } from 'react';
import ProvablyFairEngine from '../../engine/ProvablyFairEngine';
import { DEFAULT_SYMBOLS, WILD_SYMBOL, mapToGrid, buildReelStrip } from '../../engine/ReelMapper';
import { calculateWins, PAYLINES_3x3 } from '../../engine/PaytableCalculator';
import { useCasino } from '../../context/CasinoShell';
import SlotCabinet from '../SlotCabinet';

// Classic 3-reel symbols (simpler set)
const CLASSIC_SYMBOLS = [
  { id: 'forge',   name: 'Forge',   emoji: '🔥', weight: 2,  payout: { 3: 100 } },
  { id: 'anvil',   name: 'Anvil',   emoji: '⚒️',  weight: 3,  payout: { 3: 50 } },
  { id: 'hammer',  name: 'Hammer',  emoji: '🔨', weight: 5,  payout: { 3: 25 } },
  { id: 'diamond', name: 'Diamond', emoji: '💎', weight: 6,  payout: { 3: 15 } },
  { id: 'gold',    name: 'Gold',    emoji: '🥇', weight: 8,  payout: { 3: 10 } },
  { id: 'star',    name: 'Star',    emoji: '⭐',       weight: 10, payout: { 3: 5 } },
  { id: 'cherry',  name: 'Cherry',  emoji: '🍒', weight: 15, payout: { 3: 3 } },
  { id: 'bar',     name: 'Bar',     emoji: '🍺', weight: 20, payout: { 3: 2 } },
];

const BET_OPTIONS = [0.10, 0.25, 0.50, 1.00, 2.00, 5.00, 10.00];

export default function ClassicForge() {
  const { forgeBalance, placeBet, recordWin, recordLoss, logFairness, isExcluded } = useCasino();
  const engineRef = useRef(new ProvablyFairEngine());

  // Game state
  const [grid, setGrid] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [betPerLine, setBetPerLine] = useState(0.25);
  const [lastWin, setLastWin] = useState(null);
  const [lastProof, setLastProof] = useState(null);
  const [commitmentHash, setCommitmentHash] = useState(null);
  const [autoPlay, setAutoPlay] = useState(false);
  const [autoPlayCount, setAutoPlayCount] = useState(0);
  const [winAnimation, setWinAnimation] = useState(false);
  const [message, setMessage] = useState('INSERT COIN TO PLAY');
  const [spinHistory, setSpinHistory] = useState([]);
  const [showPaytable, setShowPaytable] = useState(false);

  const totalBet = betPerLine * PAYLINES_3x3.length;

  // Initialize first commitment
  useEffect(() => {
    async function init() {
      const commitment = await engineRef.current.createCommitment();
      setCommitmentHash(commitment.commitmentHash);
    }
    init();
  }, []);

  // Initialize grid with random symbols
  useEffect(() => {
    const reelStrip = buildReelStrip(CLASSIC_SYMBOLS);
    const initialGrid = [];
    for (let r = 0; r < 3; r++) {
      const reel = [];
      for (let row = 0; row < 3; row++) {
        const idx = Math.floor(Math.random() * reelStrip.length);
        reel.push(reelStrip[idx]);
      }
      initialGrid.push(reel);
    }
    setGrid(initialGrid);
  }, []);

  const spin = useCallback(async () => {
    if (spinning) return;
    if (isExcluded()) {
      setMessage('SELF-EXCLUSION ACTIVE');
      return;
    }

    // Place bet
    const betResult = placeBet(totalBet, 'classic-forge');
    if (!betResult.success) {
      setMessage(betResult.reason.toUpperCase());
      setAutoPlay(false);
      return;
    }

    setSpinning(true);
    setLastWin(null);
    setWinAnimation(false);
    setMessage('SPINNING...');

    // Generate provably fair result
    const clientSeed = Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0')).join('');

    const result = await engineRef.current.generateResult(clientSeed);
    const proof = engineRef.current.getProof();

    // Map to grid
    const newGrid = mapToGrid(result.resultValues, 3, 3, CLASSIC_SYMBOLS);

    // Animate spinning delay
    // Quick reel-by-reel stop animation
    const animFrames = 8;
    const reelStrip = buildReelStrip(CLASSIC_SYMBOLS);

    for (let frame = 0; frame < animFrames; frame++) {
      await new Promise(resolve => setTimeout(resolve, 80));
      const tempGrid = [];
      for (let r = 0; r < 3; r++) {
        if (frame >= animFrames - 3 + r) {
          // This reel has stopped — show final result
          tempGrid.push(newGrid[r]);
        } else {
          // Still spinning — show random symbols
          const reel = [];
          for (let row = 0; row < 3; row++) {
            const idx = Math.floor(Math.random() * reelStrip.length);
            reel.push(reelStrip[idx]);
          }
          tempGrid.push(reel);
        }
      }
      setGrid(tempGrid);
    }

    // Set final grid
    setGrid(newGrid);

    // Calculate wins
    const winResult = calculateWins(newGrid, betPerLine, PAYLINES_3x3);

    // Record result
    if (winResult.isWin) {
      recordWin(winResult.totalPayout, 'classic-forge', proof.resultHash);
      setLastWin(winResult);
      setWinAnimation(true);
      setMessage(`WIN! $${winResult.totalPayout.toFixed(2)}`);
      setTimeout(() => setWinAnimation(false), 2000);
    } else {
      recordLoss(totalBet, 'classic-forge', proof.resultHash);
      setMessage('TRY AGAIN');
    }

    // Log fairness
    logFairness({
      gameId: 'classic-forge',
      ...proof,
      betAmount: totalBet,
      winAmount: winResult.totalPayout,
      grid: newGrid.map(reel => reel.map(s => s.id)),
    });

    setLastProof(proof);
    setSpinHistory(prev => [{
      ...proof,
      bet: totalBet,
      win: winResult.totalPayout,
      time: new Date().toLocaleTimeString(),
    }, ...prev].slice(0, 20));

    setSpinning(false);

    // Prepare next commitment
    const nextCommitment = await engineRef.current.createCommitment();
    setCommitmentHash(nextCommitment.commitmentHash);

    // Auto-play
    if (autoPlay && autoPlayCount > 0) {
      setAutoPlayCount(prev => prev - 1);
    }
  }, [spinning, totalBet, betPerLine, placeBet, recordWin, recordLoss, logFairness, isExcluded, autoPlay, autoPlayCount]);

  // Auto-play effect
  useEffect(() => {
    if (autoPlay && autoPlayCount > 0 && !spinning) {
      const timer = setTimeout(spin, 1500);
      return () => clearTimeout(timer);
    }
    if (autoPlayCount <= 0) setAutoPlay(false);
  }, [autoPlay, autoPlayCount, spinning, spin]);

  return (
    <SlotCabinet
      title="CLASSIC FORGE"
      icon="🔥"
      theme="forge"
      stats={[
        { label: 'BALANCE', value: `$${forgeBalance.toFixed(2)}` },
        { label: 'BET', value: `$${totalBet.toFixed(2)}` },
        { label: 'LAST WIN', value: lastWin ? `$${lastWin.totalPayout.toFixed(2)}` : '-' },
      ]}
    >
      <div style={gameStyles.container}>
        {/* Commitment Hash (shown to player before spin) */}
        <div style={gameStyles.commitmentBar}>
          <span style={gameStyles.commitmentLabel}>NEXT SPIN HASH:</span>
          <span style={gameStyles.commitmentHash}>{commitmentHash ? commitmentHash.slice(0, 16) + '...' : 'Loading...'}</span>
        </div>

        {/* Reel Display */}
        <div style={gameStyles.reelContainer}>
          {grid ? grid.map((reel, r) => (
            <div key={r} style={{
              ...gameStyles.reel,
              ...(spinning ? gameStyles.reelSpinning : {}),
            }}>
              {reel.map((symbol, row) => (
                <div key={row} style={{
                  ...gameStyles.symbolCell,
                  ...(row === 1 ? gameStyles.symbolCellMiddle : {}),
                  ...(winAnimation && lastWin?.wins?.some(w =>
                    w.positions?.some(p => p.reel === r && p.row === row)
                  ) ? gameStyles.winHighlight : {}),
                }}>
                  <span style={gameStyles.symbolEmoji}>{symbol.emoji}</span>
                </div>
              ))}
            </div>
          )) : (
            <div style={gameStyles.loading}>Loading...</div>
          )}
        </div>

        {/* Payline indicators */}
        <div style={gameStyles.paylineBar}>
          {PAYLINES_3x3.map((_, i) => (
            <span key={i} style={{
              ...gameStyles.paylineDot,
              background: lastWin?.wins?.some(w => w.lineIndex === i + 1) ? '#FFD700' : '#333',
            }}>
              {i + 1}
            </span>
          ))}
        </div>

        {/* Message */}
        <div style={{
          ...gameStyles.message,
          color: winAnimation ? '#FFD700' : '#DC2626',
          animation: winAnimation ? 'slotPulse 0.3s ease-in-out infinite' : 'none',
        }}>
          {message}
        </div>

        {/* Controls */}
        <div style={gameStyles.controls}>
          {/* Bet selector */}
          <div style={gameStyles.betSection}>
            <span style={gameStyles.controlLabel}>BET/LINE</span>
            <div style={gameStyles.betButtons}>
              <button
                style={gameStyles.betBtn}
                onClick={() => {
                  const idx = BET_OPTIONS.indexOf(betPerLine);
                  if (idx > 0) setBetPerLine(BET_OPTIONS[idx - 1]);
                }}
                disabled={spinning}
              >-</button>
              <span style={gameStyles.betValue}>${betPerLine.toFixed(2)}</span>
              <button
                style={gameStyles.betBtn}
                onClick={() => {
                  const idx = BET_OPTIONS.indexOf(betPerLine);
                  if (idx < BET_OPTIONS.length - 1) setBetPerLine(BET_OPTIONS[idx + 1]);
                }}
                disabled={spinning}
              >+</button>
            </div>
          </div>

          {/* Spin button */}
          <button
            style={{
              ...gameStyles.spinBtn,
              ...(spinning ? gameStyles.spinBtnDisabled : {}),
            }}
            onClick={spin}
            disabled={spinning || forgeBalance < totalBet}
          >
            {spinning ? '⏳' : '🎰 SPIN'}
          </button>

          {/* Auto-play */}
          <div style={gameStyles.autoSection}>
            <span style={gameStyles.controlLabel}>AUTO</span>
            <div style={gameStyles.betButtons}>
              {[10, 25, 50].map(count => (
                <button
                  key={count}
                  style={{
                    ...gameStyles.autoBtn,
                    background: autoPlay && autoPlayCount > 0 ? '#DC262644' : '#1a1a1a',
                  }}
                  onClick={() => {
                    if (autoPlay) {
                      setAutoPlay(false);
                      setAutoPlayCount(0);
                    } else {
                      setAutoPlay(true);
                      setAutoPlayCount(count);
                    }
                  }}
                  disabled={spinning}
                >
                  {autoPlay ? 'STOP' : count}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom buttons */}
        <div style={gameStyles.bottomBtns}>
          <button style={gameStyles.smallBtn} onClick={() => setShowPaytable(!showPaytable)}>
            {showPaytable ? 'HIDE' : 'PAYTABLE'}
          </button>
          {lastProof && (
            <button
              style={{ ...gameStyles.smallBtn, color: '#00FF88' }}
              onClick={async () => {
                const result = await ProvablyFairEngine.verify(lastProof);
                alert(result.valid ? 'VERIFIED FAIR - Hash matches!' : `FAILED: ${result.reason}`);
              }}
            >
              VERIFY LAST SPIN
            </button>
          )}
        </div>

        {/* Paytable */}
        {showPaytable && (
          <div style={gameStyles.paytable}>
            <div style={gameStyles.paytableTitle}>PAYTABLE</div>
            {CLASSIC_SYMBOLS.map(sym => (
              <div key={sym.id} style={gameStyles.paytableRow}>
                <span>{sym.emoji} {sym.name}</span>
                <span style={{ color: '#FFD700' }}>3x = {sym.payout[3]}x</span>
              </div>
            ))}
            <div style={gameStyles.paytableNote}>
              All results are provably fair via SHA-256 cryptographic verification.
              {'\n'}RTP: ~95% | 5 paylines | Min bet: $0.10/line
            </div>
          </div>
        )}

        {/* Recent spins */}
        {spinHistory.length > 0 && (
          <div style={gameStyles.history}>
            <div style={gameStyles.historyTitle}>RECENT SPINS</div>
            {spinHistory.slice(0, 5).map((spin, i) => (
              <div key={i} style={gameStyles.historyRow}>
                <span style={{ color: '#666' }}>{spin.time}</span>
                <span style={{ color: '#DC2626' }}>-${spin.bet.toFixed(2)}</span>
                <span style={{ color: spin.win > 0 ? '#00FF88' : '#666' }}>
                  {spin.win > 0 ? `+$${spin.win.toFixed(2)}` : '-'}
                </span>
                <span style={{ color: '#444', fontSize: 8 }}>{spin.resultHash?.slice(0, 8)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </SlotCabinet>
  );
}

const gameStyles = {
  container: {
    padding: 8,
  },
  commitmentBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '6px 8px',
    background: '#0a0a0a',
    borderRadius: 6,
    marginBottom: 8,
    border: '1px solid #222',
  },
  commitmentLabel: {
    fontSize: 8,
    color: '#666',
    letterSpacing: 1,
    fontWeight: 700,
  },
  commitmentHash: {
    fontSize: 9,
    color: '#00FF88',
    fontFamily: 'monospace',
  },
  reelContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: 4,
    padding: '12px 0',
    background: 'linear-gradient(180deg, #111 0%, #000 50%, #111 100%)',
    borderRadius: 8,
    border: '2px solid #DC262644',
  },
  reel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  reelSpinning: {
    filter: 'blur(2px)',
    transition: 'filter 0.1s',
  },
  symbolCell: {
    width: 70,
    height: 70,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0a0a0a',
    borderRadius: 6,
    border: '1px solid #222',
    transition: 'all 0.2s ease',
  },
  symbolCellMiddle: {
    border: '2px solid #DC262666',
    background: '#0f0000',
  },
  winHighlight: {
    border: '2px solid #FFD700',
    background: '#1a1500',
    boxShadow: '0 0 12px #FFD70044',
  },
  symbolEmoji: {
    fontSize: 36,
  },
  paylineBar: {
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    padding: '8px 0',
  },
  paylineDot: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 9,
    fontWeight: 900,
    color: '#fff',
  },
  message: {
    textAlign: 'center',
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: 3,
    padding: '8px 0',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '8px 0',
  },
  betSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  controlLabel: {
    fontSize: 8,
    color: '#666',
    letterSpacing: 1,
    fontWeight: 700,
  },
  betButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  betBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: '1px solid #DC2626',
    background: '#1a0000',
    color: '#DC2626',
    fontSize: 16,
    fontWeight: 900,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  betValue: {
    fontSize: 14,
    color: '#FFD700',
    fontWeight: 900,
    minWidth: 50,
    textAlign: 'center',
  },
  spinBtn: {
    padding: '14px 28px',
    borderRadius: 12,
    border: '2px solid #DC2626',
    background: 'linear-gradient(180deg, #DC2626 0%, #991B1B 100%)',
    color: '#fff',
    fontSize: 16,
    fontWeight: 900,
    letterSpacing: 2,
    cursor: 'pointer',
    boxShadow: '0 0 20px #DC262644, 0 4px 12px rgba(0,0,0,0.5)',
    transition: 'all 0.2s',
  },
  spinBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  autoSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  autoBtn: {
    padding: '4px 8px',
    borderRadius: 4,
    border: '1px solid #333',
    color: '#888',
    fontSize: 9,
    fontWeight: 700,
    cursor: 'pointer',
  },
  bottomBtns: {
    display: 'flex',
    justifyContent: 'center',
    gap: 12,
    padding: '8px 0',
  },
  smallBtn: {
    background: 'none',
    border: '1px solid #333',
    borderRadius: 4,
    color: '#888',
    fontSize: 9,
    padding: '4px 10px',
    cursor: 'pointer',
    fontWeight: 700,
    letterSpacing: 1,
  },
  paytable: {
    background: '#0a0a0a',
    border: '1px solid #222',
    borderRadius: 8,
    padding: 12,
    marginTop: 8,
  },
  paytableTitle: {
    fontSize: 12,
    color: '#FFD700',
    fontWeight: 900,
    letterSpacing: 2,
    textAlign: 'center',
    marginBottom: 8,
  },
  paytableRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 8px',
    fontSize: 11,
    color: '#ccc',
    borderBottom: '1px solid #1a1a1a',
  },
  paytableNote: {
    fontSize: 8,
    color: '#555',
    textAlign: 'center',
    marginTop: 8,
    lineHeight: 1.6,
    whiteSpace: 'pre-line',
  },
  history: {
    background: '#0a0a0a',
    border: '1px solid #222',
    borderRadius: 8,
    padding: 8,
    marginTop: 8,
  },
  historyTitle: {
    fontSize: 10,
    color: '#666',
    fontWeight: 700,
    letterSpacing: 1,
    marginBottom: 6,
  },
  historyRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '3px 4px',
    fontSize: 10,
    borderBottom: '1px solid #111',
  },
};
